import { SLOT_HEADER, getActiveSlot } from './activeSlot';
import { nowMs, requestIdOf, traceApi } from '../diagnostics/trace';
import { notifyUnauthorized } from './session-events';

/**
 * DÉLAI D'EXPIRATION par défaut d'un appel `/api/*` (ms).
 *
 * Sans plafond, une requête qui PEND (proxy silencieux, bascule de réseau mobile,
 * tunnel coupé sans RST) ne rejette jamais : l'écran reste en « Chargement… » POUR
 * TOUJOURS, sans même le bouton « Réessayer » puisque le `.catch` n'est jamais
 * atteint. 60 s est délibérément GÉNÉREUX : les appels de données nominaux (page de
 * galerie, compteurs, facettes, détail d'asset) répondent en moins d'une seconde,
 * et même une requête lourde sur une très grande bibliothèque (`all-ids`,
 * `exif/analysis`) reste très en deçà. Un timeout trop court casserait des appels
 * légitimes — régression bien pire que le problème résolu.
 */
export const DEFAULT_TIMEOUT_MS = 60_000;

/**
 * Délai des appels LÉGITIMEMENT LONGS mais bornés (10 min) : parcours de système de
 * fichiers synchrones (`exif/originals/scan`, `extensions/scan`) et SONDES LLM
 * (`test-connection`, `test-vision`, `discover-models`) — un modèle local à froid
 * (LM Studio, Ollama) met couramment plusieurs minutes à répondre au premier appel.
 */
export const LONG_TIMEOUT_MS = 600_000;

/**
 * Valeur à passer en `timeoutMs` pour DÉSACTIVER tout plafond : transferts dont la
 * durée dépend de la taille des données et du débit de l'utilisateur, où AUCUNE
 * borne fixe n'est défendable — téléversement de plusieurs Go, préparation +
 * téléchargement d'une archive ZIP d'originaux. Ces appels restent annulables par
 * l'utilisateur (fermeture de l'onglet) ; les plafonner risquerait de couper un
 * upload à 95 %.
 */
export const NO_TIMEOUT = null;

/** `RequestInit` + plafond de durée propre à cet appel. */
export interface ApiFetchInit extends RequestInit {
  /**
   * Plafond de durée en ms. Omis ⇒ `DEFAULT_TIMEOUT_MS`. `null` (ou ≤ 0) ⇒ aucun
   * plafond (cf. `NO_TIMEOUT`).
   */
  timeoutMs?: number | null;
}

/**
 * Signal d'expiration pour ce plafond, ou `null` si aucun plafond n'est demandé.
 *
 * GARDE DÉFENSIVE : `AbortSignal.timeout` est standard (Node 26, navigateurs
 * courants, happy-dom des tests) mais on ne le suppose pas — absent, on dégrade en
 * « pas de timeout » plutôt que de lever au premier appel.
 */
function timeoutSignalFor(timeoutMs: number | null | undefined): AbortSignal | null {
  const effective = timeoutMs === undefined ? DEFAULT_TIMEOUT_MS : timeoutMs;
  if (effective === null || effective <= 0) return null;
  if (typeof AbortSignal === 'undefined' || typeof AbortSignal.timeout !== 'function') return null;
  return AbortSignal.timeout(effective);
}

/**
 * Compose le signal de l'APPELANT (annulation à la navigation/au démontage) avec
 * celui du timeout. `AbortSignal.any` propage l'abandon du PREMIER qui déclenche —
 * les gardes existantes des appelants (`if (controller.signal.aborted) return`)
 * restent donc exactes : sur annulation, leur propre signal est bien marqué ; sur
 * expiration, il ne l'est pas et l'erreur remonte dans leur `.catch`.
 *
 * GARDE DÉFENSIVE : sans `AbortSignal.any`, on privilégie le signal de l'appelant
 * (l'annulation au démontage est plus critique que le plafond de durée).
 */
function combineSignals(
  caller: AbortSignal | null | undefined,
  timeout: AbortSignal | null,
): AbortSignal | null {
  if (timeout === null) return caller ?? null;
  if (caller === null || caller === undefined) return timeout;
  if (typeof AbortSignal.any !== 'function') return caller;
  return AbortSignal.any([caller, timeout]);
}

/**
 * `fetch` authentifié (multi-login T7c) : joint le cookie de session (same-origin)
 * ET l'en-tête `X-Active-Slot` désignant le compte actif. À utiliser pour TOUTE
 * requête `/api/*` authentifiée (galerie, visionneuse, EXIF, admin). Les requêtes
 * publiques (`/api/health`) et browser-natives (`<img>`, téléchargement) n'en ont
 * pas besoin — ces dernières passent par `withSlotParam` (cf. `activeSlot.ts`).
 *
 * Chaque appel est TRACÉ dans les anneaux de diagnostic (métadonnées HTTP :
 * méthode/chemin assaini/statut/durée/`x-request-id`), pour l'enveloppe « Signaler
 * un bug ». Aucun corps, aucune query-string.
 *
 * Chaque appel est PLAFONNÉ en durée (`timeoutMs`, défaut `DEFAULT_TIMEOUT_MS`) :
 * une requête qui pend rejette au lieu de laisser l'écran en chargement perpétuel.
 *
 * Un **401 hors routes d'authentification** émet un événement de session
 * (`notifyUnauthorized`) que `AuthProvider` consomme pour renvoyer proprement vers
 * l'écran de connexion. Les routes `/api/auth/*` sont EXCLUES : elles répondent
 * légitimement 401 comme réponse MÉTIER (identifiants faux, slot sans session,
 * lien d'accès invalide, `change-password` avec un mot de passe actuel erroné) —
 * les y inclure provoquerait une boucle de déconnexion.
 */
export async function apiFetch(input: string, init: ApiFetchInit = {}): Promise<Response> {
  const { timeoutMs, ...rest } = init;
  const headers = new Headers(rest.headers);
  const slot = getActiveSlot();
  if (slot !== null) headers.set(SLOT_HEADER, String(slot));
  const method = (rest.method ?? 'GET').toUpperCase();
  const signal = combineSignals(rest.signal, timeoutSignalFor(timeoutMs));
  const started = nowMs();
  try {
    // `apiFetch` ne sert QUE des données (liste/détail/compteurs/facettes/admin/auth),
    // jamais des vignettes/originaux (ceux-ci passent par `<img>`/`withSlotParam`). On
    // marque donc ces requêtes « high » : elles priment sur le flot de vignettes « low »
    // (priorité de flux HTTP/2). Un appelant peut surcharger via `init.priority`.
    const res = await fetch(input, {
      credentials: 'same-origin',
      priority: 'high',
      ...rest,
      headers,
      signal,
    });
    traceApi(method, input, res.status, Math.round(nowMs() - started), requestIdOf(res));
    if (res.status === 401 && !isAuthRoute(input)) notifyUnauthorized({ slot });
    return res;
  } catch (err) {
    // Échec réseau OU expiration (pas de réponse HTTP) : trace en statut 0 pour le
    // distinguer. Les appelants convertissent ce rejet en erreur « réseau ».
    traceApi(method, input, 0, Math.round(nowMs() - started), null);
    throw err;
  }
}

/**
 * L'URL vise-t-elle une route d'AUTHENTIFICATION (`/api/auth/*`) ? Ces routes
 * répondent 401 comme réponse métier — jamais comme « votre session vient de
 * mourir ». Comparaison sur le CHEMIN seul (une query-string ne doit pas pouvoir
 * changer la décision).
 */
function isAuthRoute(input: string): boolean {
  const path = input.split('?')[0] ?? input;
  return path.startsWith('/api/auth/');
}
